import { isLikelyUrl } from "@/lib/urls";

// The rules the catalog's two item forms share, in the one place the donor
// shares them from.
//
// Request Item and Add Variant are separate forms in the source and disagree on
// several fields, but four things are genuinely one decision in the donor rather
// than two that happen to match:
//
//   - **the required message**, a bare literal repeated at every `required` rule;
//   - **the URL pattern's message**, `urlValidation.regex` + `'Invalid URL'`;
//   - **the dimension floor**, which is one exported function
//     (`validateDimensionValue`, `utils/validation.ts:6-11`) reached by both
//     forms through the shared `DimensionField`;
//   - **populate-only-if-truthy**, the shape both `buildPostData` bodies have —
//     a blank optional field is *left out*, never sent as `null` or `""`, and a
//     filled one goes as typed (see `text` — the donor trims nothing).
//
// Kept here rather than copied into both models for the reason `ItemFields`
// exists: a message or a floor that drifts between the two dialogs is a
// parity break nobody would notice, because each form's own tests would still
// pass.

/** Every `required` rule's message, verbatim, in both forms. */
export const REQUIRED_MESSAGE = "This field is required";

/**
 * A dimension is valid when blank, or when it parses to a number ≥ 0.
 *
 * An unparseable value passes, exactly as it does in the donor
 * (`validateDimensionValue` returns `true` for `NaN`) — the field is
 * `type="number"`, so the browser is what refuses "abc", and Python refuses it
 * again.
 */
export function dimensionProblem(value: string): string | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number.parseFloat(trimmed);
  if (Number.isNaN(parsed)) return undefined;
  return parsed >= 0 ? undefined : "Must be 0 or greater";
}

/**
 * The donor's `pattern: { value: urlValidation.regex, message: 'Invalid URL' }`
 * — blank passes, because the field is optional in both forms.
 *
 * **Matched on the raw value, whitespace included**, which is what
 * react-hook-form's `pattern` does: it runs against the field value as typed, so
 * ` example.com` is "Invalid URL" in modsy rather than quietly trimmed. That now
 * falls out of `isLikelyUrl` itself — the donor's regex is anchored and no class
 * in it holds a space (`utils/common.ts:136`) — where it used to need a
 * whitespace test of its own here, because the old rule handed schemed values to
 * `new URL`, which strips padding before parsing.
 *
 * It also keeps this rule coherent with `text()` below, which no longer trims:
 * a URL the form accepts is one with no whitespace to trim, so nothing reaches
 * the column that would land broken in an `href`.
 */
export function urlProblem(value: string): string | undefined {
  if (!value) return undefined;
  return isLikelyUrl(value) ? undefined : "Invalid URL";
}

/**
 * The value as typed, or `undefined` when the typist left the field blank.
 *
 * **No trim.** Both donors guard on the raw field and post the raw field —
 * `const modelNumber = data.model_number; if (modelNumber) { postData.modelNumber
 * = modelNumber; }` (`useVariantForm.ts:109-112`, and the same chain in
 * `RequestItemIsland`) — and neither island calls `trim` anywhere. Python does
 * not strip these either (`ItemAddVariantInput` declares plain `str | None`), so
 * a client-side trim is observable: modsy stores `'  M-1  '` where a trimming
 * port stores `'M-1'`.
 *
 * A whitespace-only field is therefore *sent*, not dropped, because `'   '` is
 * truthy in the donor's guard too.
 */
export const text = (value: string): string | undefined => value || undefined;

/**
 * `{ key: value }` when the field was filled, `{}` when it was not.
 *
 * The spread form of the donor's `if (modelNumber) { postData.modelNumber = … }`
 * — which is what keeps an untouched field out of the payload rather than
 * sending an empty string the column would then hold.
 */
export const when = <K extends string>(key: K, value: string | undefined) =>
  value === undefined ? {} : ({ [key]: value } as Record<K, string>);
